@if (editingEntity(); as entity) {
<div class="w-full mx-auto space-y-6 pb-8">

    <!-- Titre -->
    <h1 class="text-2xl font-bold text-gray-900 text-center">{{ 'vues.qr-code.visitor-checkin.title' | translate }}</h1>

    <!-- Section informations visiteur -->
    <div class="bg-white rounded-sm shadow-sm border border-gray-200 p-4 space-y-4">

        @if (showCamera()) {
        <app-photo-capture
            (photoCaptured)="onPhotoCaptured($event)"
            (photoFile)="onPhotoFile($event)">
        </app-photo-capture>
        }

        <app-field-wrapper [property]="'visitor.fullname'"
            [label]="'entities.visitor.propreties.fullname'"
            [labelPosition]="'above'"
            [entity]="editingEntity()"
            [isEditMode]="isEditMode()"
            [flashingProperty]="flashingProperty()"
            [index]="getModifiedFieldIndex('visitor.fullname')"
            [totalModified]="totalModifiedCount()"
            [toggleUndo]="toggleUndo.bind(this)"
            [triggerFlash]="triggerFlash.bind(this)"
            [hasValidationErrors]="hasValidationErrors.bind(this)"
            [getValidationErrors]="getValidationErrors.bind(this)"
            [propretyHasChanged]="propretyHasChanged.bind(this)"
            [skin]="Skin">
            <app-multi-select
                [options]="visitorOptions()"
                [isMultiselect]="false"
                [minChars]="3"
                [allowCreate]="true"
                [skin]="Skin"
                [placeholder]="isEmployee() ? 'Select Employee' : 'Select Visitor'"
                [maxItems]="1"
                [selectedKeys]="editingEntity()?.visitor?.selectedPK_Visitor ?? []"
                (selectedKeysChange)="onVisitorSelectChange($event)"
                (createNew)="onCreateVisitor($event)">
            </app-multi-select>
        </app-field-wrapper>

        @if (showCompany()) {
        <app-field-wrapper [property]="'company'"
            [label]="'entities.visit.propreties.company'"
            [labelPosition]="'above'"
            [entity]="editingEntity()"
            [isEditMode]="isEditMode()"
            [flashingProperty]="flashingProperty()"
            [index]="getModifiedFieldIndex('company')"
            [totalModified]="totalModifiedCount()"
            [toggleUndo]="toggleUndo.bind(this)"
            [triggerFlash]="triggerFlash.bind(this)"
            [hasValidationErrors]="hasValidationErrors.bind(this)"
            [getValidationErrors]="getValidationErrors.bind(this)"
            [propretyHasChanged]="propretyHasChanged.bind(this)"
            [skin]="Skin">
            <input type="text" autocomplete="new-password" onReady
                [class.bg-yellow-200]="flashingProperty() === 'company'"
                class="{{Skin.formInputSmallSize}}"
                (autoBind)="bindingHandler($event, 'company')">
        </app-field-wrapper>
        }

        @if (showPhone()) {
        <app-field-wrapper [property]="'phoneNumber'"
            [label]="'entities.visitor.propreties.phoneNumber'"
            [labelPosition]="'above'"
            [entity]="editingEntity()"
            [isEditMode]="isEditMode()"
            [flashingProperty]="flashingProperty()"
            [index]="getModifiedFieldIndex('phoneNumber')"
            [totalModified]="totalModifiedCount()"
            [toggleUndo]="toggleUndo.bind(this)"
            [triggerFlash]="triggerFlash.bind(this)"
            [hasValidationErrors]="hasValidationErrors.bind(this)"
            [getValidationErrors]="getValidationErrors.bind(this)"
            [propretyHasChanged]="propretyHasChanged.bind(this)"
            [skin]="Skin">
            <input type="text" autocomplete="new-password" onReady
                [class.bg-yellow-200]="flashingProperty() === 'phoneNumber'"
                class="{{Skin.formInputSmallSize}}"
                (autoBind)="bindingHandler($event, 'phoneNumber')">
        </app-field-wrapper>
        }

        <app-field-wrapper [property]="'selectedPK_Employee'"
            [labelPosition]="'above'"
            [label]="'entities.visit.propreties.employee'"
            [entity]="editingEntity()"
            [isEditMode]="isEditMode()"
            [flashingProperty]="flashingProperty()"
            [totalModified]="totalModifiedCount()"
            [toggleUndo]="toggleUndo.bind(this)"
            [triggerFlash]="triggerFlash.bind(this)"
            [hasValidationErrors]="hasValidationErrors.bind(this)"
            [getValidationErrors]="getValidationErrors.bind(this)"
            [propretyHasChanged]="propretyHasChanged.bind(this)"
            [skin]="Skin">
            <app-multi-select #multiSelect
                [options]="employeesLookup()"
                [isMultiselect]="false"
                [minChars]="3"
                [skin]="Skin"
                [placeholder]="'Select Employee'"
                [maxItems]="1"
                [selectedKeys]="editingEntity()?.selectedPK_Employee ?? []"
                (selectedKeysChange)="onEmployeeSelectChange($event)"
                onReady
                (autoBind)="bindingHandler($event, 'selectedPK_Employee', multiSelect)">
            </app-multi-select>
        </app-field-wrapper>
    </div>

    <!-- Politique -->
    @if (isStepVisible('policy')) {
    <app-policy-viewer
        [url]="urlPolitique"
        [showSignature]="false"
        (accepted)="onPolicyViewerAccepted($event)">
    </app-policy-viewer>
    }

    <!-- Engagements -->
    @if (isStepVisible('engagement')) {
    <app-engagement-viewer
        [items]="getEngagementItems()"
        [showSignature]="false"
        [requireSignature]="false"
        (accepted)="onEngagementAccepted($event)">
    </app-engagement-viewer>
    }

    <!-- Santé / Covid -->
    @if (isStepVisible('healthy')) {
    <app-healthy-viewer
        [items]="getCovidItems()"
        [showSignature]="false"
        [requireSignature]="false"
        (accepted)="onHealthyAccepted($event)"
        (refused)="onHealthyRefused()">
    </app-healthy-viewer>
    }

    <!-- Quiz -->
    @if (isStepVisible('quiz')) {
    <app-quiz-viewer
        [items]="getQuizItems()"
        [nbQuestions]="$safeNavigationMigration(currentPos?.quiz_NbQuestion)"
        [random]="currentPos?.quiz_Random ?? false"
        (quizCompleted)="onQuizCompleted($event)">
    </app-quiz-viewer>
    }

    <!-- Signature unique (requise dès qu'un document à signer est affiché) -->
    @if (signatureRequired()) {
    <div class="bg-white rounded-sm shadow-sm border border-gray-200 p-4">
        <app-signature-pad
            [height]="200"
            [placeholder]="'vues.ipad.signaturePlaceHolder' | translate"
            (signatureChange)="onSignatureChange($event)">
        </app-signature-pad>
    </div>
    }

    <!-- Actions : affichées une fois le visiteur identifié (créé ou vérifié) -->
    @if (visitorIdentified()) {
    <div class="flex flex-col gap-3 pt-2">
        <button
            class="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
            [disabled]="!canSubmit()"
            (click)="saveCheckin()">
            {{ 'buttons.submit' | translate }}
        </button>
        <button
            class="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-sm transition"
            (click)="goHome()">
            {{ 'buttons.cancel' | translate }}
        </button>
    </div>
    }

</div>
}